import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * Auto-hiding stationery share bar.
 * - Hides on scroll-down, returns on scroll-up (Apple Mail style)
 * - On mobile: collapses to a single 36px envelope icon — taps to expand
 * - Glass-morphism with subtle paper texture so it feels part of the invitation, not floating UI
 */
export default function ShareBar({ groomName, brideName, weddingDate, slug }) {
  const [copied, setCopied] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const lastY = useRef(0);

  const url = typeof window !== "undefined" ? window.location.href : `https://weddinsite.com/i/${slug}`;
  const dateText = weddingDate ? new Date(weddingDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";
  const message = `You are cordially invited to the wedding of ${groomName} & ${brideName} on ${dateText}. With love, please open the invitation: ${url}`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y > 80 && y > lastY.current + 4) {
        setHidden(true);
        setExpanded(false);
      } else if (y < lastY.current - 4) {
        setHidden(false);
      }
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Invitation link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Could not copy"); }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `${groomName} & ${brideName} · Wedding`, text: message, url }); }
      catch { /* user cancelled */ }
    } else copy();
  };

  return (
    <div
      className={`fixed top-3 right-3 z-40 transition-all duration-300 ${hidden ? "translate-y-[-180%] opacity-0 pointer-events-none" : "translate-y-0 opacity-100"}`}
      data-testid="share-bar"
    >
      {/* Mobile: collapsed envelope icon */}
      <button
        onClick={() => setExpanded((v) => !v)}
        aria-label="Share invitation"
        data-testid="share-toggle"
        className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full bg-white/95 backdrop-blur shadow-md text-stone-800 ${expanded ? "ring-2 ring-amber-400" : ""}`}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 7l10 7 10-7"/></svg>
      </button>

      {/* Desktop bar OR mobile expanded panel */}
      <div className={`${expanded ? "flex absolute top-12 right-0" : "hidden"} md:flex items-center gap-1.5 bg-white/85 backdrop-blur border border-stone-200/60 px-1.5 py-1 rounded-full shadow-md`}>
        <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-share" aria-label="Share on WhatsApp" className="flex items-center gap-1.5 px-3 py-2 bg-[#25D366] hover:bg-[#1ebe5a] text-white text-[10px] uppercase tracking-[0.2em] rounded-full transition-transform hover:scale-105">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.413c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26l.346.55-.971 3.547 3.604-.946z"/></svg>
          WA
        </a>
        <button onClick={copy} data-testid="copy-link" aria-label="Copy invitation link" className="px-3 py-2 hover:bg-stone-100 text-stone-800 text-[10px] uppercase tracking-[0.2em] rounded-full transition-transform hover:scale-105">
          {copied ? "✓" : "Copy"}
        </button>
        {typeof navigator !== "undefined" && navigator.share && (
          <button onClick={nativeShare} data-testid="native-share" aria-label="Share" className="md:hidden px-2.5 py-2 hover:bg-stone-100 text-stone-800 rounded-full">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
          </button>
        )}
      </div>
    </div>
  );
}
